import type { DanceKey } from '@dn/shared';
import * as THREE from 'three';
import type { CharacterModel } from './CharacterModel';
import type { Collider } from '../physics/Collision';
import { resolveCircle } from '../physics/Collision';

export const WALK_SPEED = 3.6;
export const RUN_SPEED = 7; // server cho phép tối đa 9 m/s (+ dung sai)

/** Điều khiển nhân vật: di chuyển theo hướng camera, xoay mượt, va chạm, bám địa hình. */
export class PlayerController {
  readonly position = new THREE.Vector3();
  rotY = Math.PI;
  speed = 0;
  private velocity = new THREE.Vector3();
  autoTarget: THREE.Vector3 | null = null; // tự động di chuyển tới mục tiêu
  /** Tư thế giữ khi đứng yên (ngồi ghế rạp, nhảy ở sân khấu) – tự hủy khi di chuyển */
  pose: 'sit' | DanceKey | null = null;
  /** hệ số tốc độ (món ăn Phố Hội) – ≤ 1.25 để không vượt giới hạn server 9 m/s */
  speedMul = 1;
  private stuckT = 0; private detourT = 0; private detourSide = 1; private detourN = 0;

  constructor(public model: CharacterModel, private heightAt: (x: number, z: number) => number, private colliders: Collider[], private bound = 78) {}

  setWorld(heightAt: (x: number, z: number) => number, colliders: Collider[]) { this.heightAt = heightAt; this.colliders = colliders; }

  teleport(x: number, z: number, rotY = this.rotY) {
    this.position.set(x, this.heightAt(x, z), z); this.rotY = rotY; this.velocity.set(0, 0, 0); this.autoTarget = null; this.sync();
  }

  update(dt: number, input: { x: number; y: number; run: boolean }, camForward: THREE.Vector3) {
    const right = new THREE.Vector3().crossVectors(camForward, new THREE.Vector3(0, 1, 0)).normalize();
    const wish = new THREE.Vector3().addScaledVector(camForward, input.y).addScaledVector(right, input.x);
    let run = input.run;
    if (wish.lengthSq() > 0.0001) { this.autoTarget = null; this.pose = null; }
    else if (this.autoTarget) {
      this.pose = null;
      const d = this.autoTarget.clone().sub(this.position).setY(0);
      const dist = d.length();
      if (dist < 1.9) { this.autoTarget = null; this.detourN = 0; }
      else {
        wish.copy(d.normalize()); run = dist > 6;
        // tránh vật cản đơn giản: kẹt > 0.4s ⇒ lách về một phía, mỗi lần kẹt lại lách lâu hơn;
        // kẹt ngay trong lúc lách (gặp tường) ⇒ đổi phía.
        if (this.stuckT > 0.4) {
          if (this.detourT > 0) this.detourSide = -this.detourSide;
          this.detourN = Math.min(this.detourN + 1, 6); this.detourT = 0.6 + 0.45 * this.detourN; this.stuckT = 0;
        }
        if (this.detourT > 0) { this.detourT -= dt; wish.applyAxisAngle(new THREE.Vector3(0, 1, 0), this.detourSide * 1.2); }
      }
    }
    const mag = Math.min(1, wish.length());
    const target = wish.lengthSq() > 0 ? wish.normalize().multiplyScalar((run ? RUN_SPEED : WALK_SPEED) * Math.min(1.25, this.speedMul) * Math.max(mag, 0.35)) : new THREE.Vector3();
    this.velocity.lerp(target, 1 - Math.exp(-dt * 12));
    const next = this.position.clone().addScaledVector(this.velocity, dt);
    const r = resolveCircle(next.x, next.z, 0.38, this.colliders);
    next.x = THREE.MathUtils.clamp(r.x, -this.bound, this.bound); next.z = THREE.MathUtils.clamp(r.z, -this.bound, this.bound);
    const gy = this.heightAt(next.x, next.z);
    next.y = this.position.y + (gy - this.position.y) * Math.min(1, dt * 18); // làm mượt khi lên/xuống bậc
    this.speed = next.clone().sub(this.position).setY(0).length() / Math.max(dt, 1e-4);
    if (this.autoTarget && this.speed < 0.6) this.stuckT += dt; else this.stuckT = 0;
    this.position.copy(next);
    if (this.velocity.lengthSq() > 0.05) {
      const want = Math.atan2(this.velocity.x, this.velocity.z);
      let d = want - this.rotY; d = Math.atan2(Math.sin(d), Math.cos(d)); this.rotY += d * Math.min(1, dt * 12);
    }
    this.model.setAnim(this.animName());
    this.sync();
  }

  faceTowards(x: number, z: number) { this.rotY = Math.atan2(x - this.position.x, z - this.position.z); this.sync(); }

  private sync() { this.model.root.position.copy(this.position); this.model.root.rotation.y = this.rotY; }

  animName(): 'idle' | 'walk' | 'run' | 'sit' | DanceKey { return this.speed > 5.5 ? 'run' : this.speed > 0.4 ? 'walk' : this.pose ?? 'idle'; }
}
